// ============================================================
// hooks/useLocation.js — Geolocation + Haversine distance helper
// ============================================================

import { useEffect, useState } from "react";

// Office allowed area — pulled from env vars
export const allowedArea = {
  lat:    process.env.REACT_APP_OFFICE_LATITUDE,
  lng:    process.env.REACT_APP_OFFICE_LONGITUDE,
  radius: 10000, // meters
};

/**
 * Haversine formula — returns distance in meters between two lat/lng points.
 */
export const getDistance = (lat1, lon1, lat2, lon2) => {
  const R     = 6371000; // Earth radius in metres
  const toRad = (deg) => (deg * Math.PI) / 180;

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/**
 * Returns { userLocation, locationError }
 * Requests the browser's geolocation once on mount.
 */
const useLocation = () => {
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported in this browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
      },
      (err) => {
        console.error("Error getting location:", err);
        setLocationError("Location access denied. Please enable it.");
      }
    );
  }, []);

  return { userLocation, locationError };
};

export default useLocation;